import { Fragment, useCallback, useId } from 'react';

import { css, styled } from '@mui/material';

import { StackDirection } from '../../constants/stack-direction';
import { useCascadeState } from '../../utils';
import { RadioItem } from '../RadioItem';
import { radioGroupClasses } from './theme';

import type { ChangeEvent } from 'react';
import type { RadioGroupOwnerState, RadioGroupProps } from './types';

export function RadioGroup<V = unknown, ExtraOption extends Record<string, any> = {}>(
  props: RadioGroupProps<V, ExtraOption>,
) {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 컴포넌트에서 직접 처리
  const {
    className,
    direction = StackDirection.Vertical, // theme defaultProps에서 이동
    disabled: disabledProp,
    error: errorProp,
    id: idProp,
    inputRef,
    name,
    onChange: onChangeProp,
    options,
    ref,
    renderOption,
    style,
    subTextDirection: subTextDirectionProp,
    value: valueProp,
    ...others
  } = props;

  const fallbackId = useId();
  const id = idProp ?? fallbackId;

  const optionIdPrefix = `${id}-option-`;
  const getOptionComputedId = (index: number) => `${optionIdPrefix}${index}`;

  const ownerState: RadioGroupOwnerState = {
    ...props,
    direction, // 기본값이 적용된 변수를 ownerState에 재조합
  };

  const [value, onChange] = useCascadeState({
    initialValue: null,
    value: valueProp,
    onChange: (value: V | null, event: ChangeEvent<HTMLInputElement>) => {
      if (value !== null) {
        onChangeProp?.(event, value);
      }
    },
  });

  const getOptionChecked = (optionValue: V) => {
    return value === optionValue;
  };

  const handleOptionChange = useCallback(
    (optionValue: V) => (event: ChangeEvent<HTMLInputElement>) => {
      onChange(optionValue, event);
    },
    [onChange],
  );

  const handleOptionSelect = useCallback(
    (optionValue: V) => () => {
      const syntheticEvent = {
        target: { value: String(optionValue) },
        currentTarget: { value: String(optionValue) },
      } as ChangeEvent<HTMLInputElement>;
      onChange(optionValue, syntheticEvent);
    },
    [onChange],
  );

  return (
    <RadioGroupRoot
      className={className}
      id={id}
      ref={ref}
      style={style}
      ownerState={ownerState}
      {...others}
    >
      {options.map((option, optionIndex) => {
        const {
          disabled = disabledProp,
          error = errorProp,
          info,
          status,
          text,
          subText,
          subTextDirection = subTextDirectionProp,
          value: optionValue,
          disabledTooltip,
        } = option;

        const checked = getOptionChecked(optionValue);

        if (renderOption) {
          const optionId = getOptionComputedId(optionIndex);

          const inputElement = (
            <RadioGroupHiddenInput
              type="radio"
              checked={checked}
              disabled={disabled}
              id={optionId}
              name={name}
              onChange={handleOptionChange(optionValue)}
              ref={inputRef}
              value={String(optionValue)}
              ownerState={ownerState}
            />
          );

          return (
            <Fragment key={optionIndex}>
              {renderOption({
                checked,
                disabled: disabled ?? false,
                error: error ?? false,
                htmlFor: optionId,
                inputElement,
                onChange: handleOptionChange(optionValue),
                onSelect: handleOptionSelect(optionValue),
                disabledTooltip,
                option,
              })}
            </Fragment>
          );
        }

        return (
          <RadioGroupOption
            checked={checked}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
            error={error}
            info={info}
            inputRef={inputRef}
            key={optionIndex}
            name={name}
            onChange={(event, checked) => checked && onChange(optionValue, event)}
            status={status}
            text={text}
            subText={subText}
            subTextDirection={subTextDirection}
            value={optionValue}
            ownerState={ownerState}
          />
        );
      })}
    </RadioGroupRoot>
  );
}

const RadioGroupRoot = styled('div', {
  target: radioGroupClasses.root, // target은 유지
})<{ ownerState: RadioGroupOwnerState }>`
  /* theme.ts에서 이전된 스타일들 */
  display: flex;

  ${({ ownerState }) => {
    const { direction = StackDirection.Vertical } = ownerState;
    const flexDirection = StackDirection.toFlexDirection(direction);
    return css`
      flex-direction: ${flexDirection};
      gap: ${flexDirection === 'row' ? '20px' : '4px'};
    `;
  }}
`;

const RadioGroupOption = styled(RadioItem, {
  target: radioGroupClasses.option, // target은 유지
})<{ ownerState: RadioGroupOwnerState }>`
  /* option 관련 스타일이 필요한 경우 추가 */
`;

const RadioGroupHiddenInput = styled('input', {
  name: 'RadioGroup',
  slot: 'hiddenInput',
  target: radioGroupClasses.hiddenInput,
})<{ ownerState: RadioGroupOwnerState }>(
  ({ theme }) => css`
    ${theme.mixins.visuallyHidden}
  `,
);
